import { create } from 'zustand';
import { produce } from 'immer';
import { subjectModelsApi } from '@/lib/api/subject-models';
import { dedupeSubjectModels } from '@/lib/asset-entity-lists';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';
import type { CreateSubjectModelRequest, SubjectModel, SubjectModelType } from '@/types/subject-model';

interface SubjectModelsState {
  models: Record<SubjectModelType, SubjectModel[]>;
  isLoading: Record<SubjectModelType, boolean>;
  isCreating: boolean;
  error: string | null;
  lastLoadedAt: Record<SubjectModelType, number | null>;
  ensureLoaded: (modelType: SubjectModelType) => Promise<void>;
  refresh: (modelType: SubjectModelType) => Promise<void>;
  registerSavedModel: (model: SubjectModel) => void;
  createModel: (payload: CreateSubjectModelRequest) => Promise<SubjectModel>;
  retryAnalysis: (id: string, modelType: SubjectModelType) => Promise<SubjectModel>;
  archiveModel: (id: string, modelType: SubjectModelType) => Promise<void>;
  clearError: () => void;
}

const SUBJECT_MODEL_LIST_TTL_MS = 60_000;
const listRequests = new Map<SubjectModelType, Promise<void>>();
const removedModels = new Set<string>();

function keepLatestModel(incoming: SubjectModel, current?: SubjectModel): SubjectModel {
  if (!current) return incoming;
  const incomingAt = Date.parse(incoming.updated_at);
  const currentAt = Date.parse(current.updated_at);
  if (Number.isFinite(incomingAt) && Number.isFinite(currentAt) && incomingAt < currentAt) return current;
  // Soul admission receipts can arrive after the same training has completed.
  // The identity's immutable task must never regress to an in-progress snapshot.
  if (current.character_kind === 'soul_id' && incoming.character_kind === 'soul_id'
    && current.soul_identity?.task_id
    && current.soul_identity?.task_id === incoming.soul_identity?.task_id
    && ['completed', 'failed'].includes(current.analysis_status)
    && ['pending', 'analyzing'].includes(incoming.analysis_status)) return current;
  return incoming;
}
const SUBJECT_MODEL_ERROR = {
  LOAD_FAILED: 'subjectModelLoadFailed',
  CREATE_FAILED: 'subjectModelCreateFailed',
  RETRY_FAILED: 'subjectModelRetryAnalysisFailed',
  DELETE_FAILED: 'subjectModelDeleteFailed',
} as const;

const emptyModelBuckets = (): Record<SubjectModelType, SubjectModel[]> => ({
  product: [],
  character: [],
});

const emptyLoadingBuckets = (): Record<SubjectModelType, boolean> => ({
  product: false,
  character: false,
});

const emptyLoadedAtBuckets = (): Record<SubjectModelType, number | null> => ({
  product: null,
  character: null,
});

export const useSubjectModelsStore = create<SubjectModelsState>((set, get) => ({
  models: emptyModelBuckets(),
  isLoading: emptyLoadingBuckets(),
  isCreating: false,
  error: null,
  lastLoadedAt: emptyLoadedAtBuckets(),

  ensureLoaded: async (modelType) => {
    const state = get();
    if (listRequests.has(modelType)) return listRequests.get(modelType);
    const lastLoadedAt = state.lastLoadedAt[modelType];
    const awaitingTraining = state.models[modelType].some(model => ['pending', 'analyzing'].includes(model.analysis_status));
    if (!awaitingTraining && !state.error && lastLoadedAt && Date.now() - lastLoadedAt < SUBJECT_MODEL_LIST_TTL_MS) return;
    await get().refresh(modelType);
  },

  registerSavedModel: (model) => {
    if (removedModels.has(model.id)) return;
    const current = get().models[model.model_type].find(item => item.id === model.id);
    const latest = keepLatestModel(model, current);
    set(produce<SubjectModelsState>((draft) => {
      draft.models[model.model_type] = dedupeSubjectModels([latest, ...draft.models[model.model_type].filter(item => item.id !== model.id)]);
    }));
  },

  refresh: (modelType) => {
    const existing = listRequests.get(modelType);
    if (existing) return existing;
    const scope = getAuthScopeRevision();
    const modelsAtStart = new Map(get().models[modelType].map(model => [model.id, model]));
    const request = Promise.resolve().then(async () => {
      if (scope !== getAuthScopeRevision()) return;
      set(produce<SubjectModelsState>((draft) => {
        draft.isLoading[modelType] = true;
        draft.error = null;
      }));
      try {
        const response = await subjectModelsApi.list(modelType);
        if (scope !== getAuthScopeRevision()) return;
        if (response.success === false || response.error || !response.data) {
          set(produce<SubjectModelsState>((draft) => {
            draft.isLoading[modelType] = false;
            draft.error = SUBJECT_MODEL_ERROR.LOAD_FAILED;
          }));
          return;
        }
        const current = get().models[modelType];
        const savedDuringLoad = current.filter(model => modelsAtStart.get(model.id) !== model);
        const currentById = new Map(current.map(model => [model.id, model]));
        const responseById = new Map(response.data.map(model => [model.id, model]));
        const models = dedupeSubjectModels([
          ...savedDuringLoad.map(model => responseById.has(model.id) ? keepLatestModel(responseById.get(model.id)!, model) : model),
          ...response.data.filter(model => !savedDuringLoad.some(saved => saved.id === model.id))
            .map(model => keepLatestModel(model, currentById.get(model.id))),
        ]).filter(model => !removedModels.has(model.id));
        set(produce<SubjectModelsState>((draft) => {
          draft.models[modelType] = models;
          draft.isLoading[modelType] = false;
          draft.error = null;
          draft.lastLoadedAt[modelType] = Date.now();
        }));
      } catch {
        if (scope !== getAuthScopeRevision()) return;
        set(produce<SubjectModelsState>((draft) => {
          draft.isLoading[modelType] = false;
          draft.error = SUBJECT_MODEL_ERROR.LOAD_FAILED;
        }));
      }
    });
    listRequests.set(modelType, request);
    void request.finally(() => { if (listRequests.get(modelType) === request) listRequests.delete(modelType); });
    return request;
  },

  createModel: async (payload) => {
    const scope = getAuthScopeRevision();
    set(produce<SubjectModelsState>((draft) => {
      draft.isCreating = true;
      draft.error = null;
    }));
    try {
      const response = await subjectModelsApi.create(payload);
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.CREATE_FAILED);
      if (response.success === false || response.error || !response.data) {
        set(produce<SubjectModelsState>((draft) => {
          draft.isCreating = false;
          draft.error = SUBJECT_MODEL_ERROR.CREATE_FAILED;
        }));
        throw new Error(SUBJECT_MODEL_ERROR.CREATE_FAILED);
      }
      const model = response.data;
      get().registerSavedModel(model);
      set(produce<SubjectModelsState>((draft) => {
        draft.isCreating = false;
        draft.error = null;
        draft.lastLoadedAt[model.model_type] = Date.now();
      }));
      return model;
    } catch {
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.CREATE_FAILED);
      set(produce<SubjectModelsState>((draft) => {
        draft.isCreating = false;
        draft.error = SUBJECT_MODEL_ERROR.CREATE_FAILED;
      }));
      throw new Error(SUBJECT_MODEL_ERROR.CREATE_FAILED);
    }
  },

  retryAnalysis: async (id, modelType) => {
    const scope = getAuthScopeRevision();
    try {
      const response = await subjectModelsApi.retryAnalysis(id);
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.RETRY_FAILED);
      if (response.success === false || response.error || !response.data) {
        throw new Error(SUBJECT_MODEL_ERROR.RETRY_FAILED);
      }
      get().registerSavedModel(response.data);
      set(produce<SubjectModelsState>((draft) => {
        draft.error = null;
        draft.lastLoadedAt[modelType] = Date.now();
      }));
      return response.data;
    } catch {
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.RETRY_FAILED);
      set(produce<SubjectModelsState>((draft) => {
        draft.error = SUBJECT_MODEL_ERROR.RETRY_FAILED;
      }));
      throw new Error(SUBJECT_MODEL_ERROR.RETRY_FAILED);
    }
  },

  archiveModel: async (id, modelType) => {
    const scope = getAuthScopeRevision();
    try {
      const response = await subjectModelsApi.archive(id);
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.DELETE_FAILED);
      if (response.success === false || response.error) {
        throw new Error(SUBJECT_MODEL_ERROR.DELETE_FAILED);
      }
      removedModels.add(id);
      set(produce<SubjectModelsState>((draft) => {
        draft.models[modelType] = draft.models[modelType].filter((model) => model.id !== id);
        draft.lastLoadedAt[modelType] = Date.now();
      }));
    } catch {
      if (scope !== getAuthScopeRevision()) throw new Error(SUBJECT_MODEL_ERROR.DELETE_FAILED);
      set(produce<SubjectModelsState>((draft) => {
        draft.error = SUBJECT_MODEL_ERROR.DELETE_FAILED;
      }));
      throw new Error(SUBJECT_MODEL_ERROR.DELETE_FAILED);
    }
  },

  clearError: () => {
    set(produce<SubjectModelsState>((draft) => {
      draft.error = null;
    }));
  },
}));

registerAuthScopedCacheReset('subject-models', () => {
  listRequests.clear();
  removedModels.clear();
  useSubjectModelsStore.setState({
    models: emptyModelBuckets(), isLoading: emptyLoadingBuckets(), isCreating: false,
    error: null, lastLoadedAt: emptyLoadedAtBuckets(),
  });
});
